import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import infrastructureMonitoringService from '../../../services/infrastructureMonitoringService';

const AlertsManagementPanel = ({ alerts, onRefresh }) => {
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-red-600 bg-red-50 border-red-200',
      high: 'text-orange-600 bg-orange-50 border-orange-200',
      medium: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      low: 'text-blue-600 bg-blue-50 border-blue-200'
    };
    return colors?.[severity] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getSeverityIcon = (severity) => {
    const icons = {
      critical: 'XCircle',
      high: 'AlertTriangle',
      medium: 'AlertCircle',
      low: 'Info'
    };
    return icons?.[severity] || 'Info';
  };

  const handleResolveAlert = async (alertId) => {
    try {
      setUpdating(true);
      await infrastructureMonitoringService?.updateAlertStatus(alertId, 'resolved', resolutionNotes);
      setSelectedAlert(null);
      setResolutionNotes('');
      onRefresh?.();
    } catch (error) {
      console.error('Error resolving alert:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      setUpdating(true);
      await infrastructureMonitoringService?.updateAlertStatus(alertId, 'acknowledged');
      onRefresh?.();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div>
      {alerts?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="CheckCircle" className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <p className="text-gray-500">No active alerts - all systems operational</p>
        </div>
      ) : (
        <div className="space-y-4">
          {alerts?.map((alert) => (
            <div
              key={alert?.id}
              className={`border rounded-lg p-6 ${getSeverityColor(alert?.severity)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <Icon
                    name={getSeverityIcon(alert?.severity)}
                    className="w-6 h-6 flex-shrink-0 mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{alert?.title}</h3>
                      <span className="px-2 py-1 text-xs font-medium rounded uppercase">
                        {alert?.severity}
                      </span>
                    </div>
                    <p className="text-sm mb-3">{alert?.message}</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Affected Component:</span>
                        <span className="ml-2">{alert?.affectedComponent}</span>
                      </div>
                      <div>
                        <span className="font-medium">Alert Type:</span>
                        <span className="ml-2">{alert?.alertType}</span>
                      </div>
                      {alert?.thresholdValue && (
                        <div>
                          <span className="font-medium">Threshold:</span>
                          <span className="ml-2">{alert?.thresholdValue}</span>
                        </div>
                      )}
                      {alert?.currentValue && (
                        <div>
                          <span className="font-medium">Current Value:</span>
                          <span className="ml-2 font-semibold">{alert?.currentValue}</span>
                        </div>
                      )}
                      <div>
                        <span className="font-medium">Escalation Level:</span>
                        <span className="ml-2">{alert?.escalationLevel}</span>
                      </div>
                      <div>
                        <span className="font-medium">Created:</span>
                        <span className="ml-2">{new Date(alert?.createdAt)?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {alert?.alertStatus === 'active' && (
                    <>
                      <button
                        onClick={() => handleAcknowledgeAlert(alert?.id)}
                        disabled={updating}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      >
                        Acknowledge
                      </button>
                      <button
                        onClick={() => setSelectedAlert(alert)}
                        disabled={updating}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        Resolve
                      </button>
                    </>
                  )}
                </div>
              </div>

              {selectedAlert?.id === alert?.id && (
                <div className="mt-4 pt-4 border-t">
                  <label className="block text-sm font-medium mb-2">
                    Resolution Notes
                  </label>
                  <textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e?.target?.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter resolution notes..."
                  />
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      onClick={() => handleResolveAlert(alert?.id)}
                      disabled={updating}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {updating ? 'Resolving...' : 'Confirm Resolution'}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedAlert(null);
                        setResolutionNotes('');
                      }}
                      disabled={updating}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertsManagementPanel;